import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  catalogs,
  members,
  products,
  type Address,
  type Broker,
  type BrokerCnpjStatus,
  type BrokerInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatDateTime,
  formatDocument,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

import {
  AVISO_REGRAS_PENDENTES,
  PENDENCIA_DISPENSAS,
  PERMISSAO_COMISSAO,
  PERMISSAO_EDITAR,
  situacoesDoCnpj,
  situacoesSusep,
} from './rotulos';

/**
 * Subaba "Dados cadastrais" do Cadastro Completo, na ordem do OnPoint: CNPJ, Razao
 * Social, Inscricao Municipal, Comissao, Limite de Comissao, Situacao do CNPJ, Codigo
 * CNAE, Ramo, Data do registro, Data do recadastro, Data expiracao do cadastro,
 * Situacao SUSEP, Codigo SUSEP, Telefone e o endereco; depois os campos do Anexo C
 * (Canal, codigos da seguradora, Level corretor) e o que o SeguraCore ja tinha.
 *
 * O CNPJ fica so de leitura depois do cadastro, como no OnPoint (decisao 14 da secao
 * 11.8 do plano 49). A comissao e o limite pedem a alcada propria; sem ela, ficam
 * travados. O bloqueio tem caminho proprio, com a justificativa.
 */
export function BrokerRegistrationForm({ corretor }: { corretor: Broker }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can(PERMISSAO_EDITAR);
  const podeComissao = podeEditar && can(PERMISSAO_COMISSAO);

  const [razaoSocial, setRazaoSocial] = useState(corretor.legal_name);
  const [nomeFantasia, setNomeFantasia] = useState(corretor.trade_name);
  const [inscricao, setInscricao] = useState(corretor.municipal_registration);
  const [comissao, setComissao] = useState(rateToInput(corretor.commission_bps));
  const [limite, setLimite] = useState(
    corretor.max_commission_bps != null ? rateToInput(corretor.max_commission_bps) : '',
  );
  const [situacaoCnpj, setSituacaoCnpj] = useState<BrokerCnpjStatus>(
    corretor.cnpj_status ?? '',
  );
  const [cnae, setCnae] = useState(corretor.cnae_code ?? '');
  const [ramo, setRamo] = useState(corretor.cnae_branch ?? '');
  const [naturezaJuridica, setNaturezaJuridica] = useState(corretor.legal_nature ?? '');
  const [dataRegistro, setDataRegistro] = useState(corretor.susep_registered_on ?? '');
  const [dataRecadastro, setDataRecadastro] = useState(corretor.reregistered_on ?? '');
  const [dataExpiracao, setDataExpiracao] = useState(
    corretor.registration_expires_on ?? '',
  );
  const [situacaoSusep, setSituacaoSusep] = useState(corretor.external_status);
  const [codigoSusep, setCodigoSusep] = useState(corretor.susep_code);
  const [telefone, setTelefone] = useState(corretor.phone);
  const [email, setEmail] = useState(corretor.email);
  const [contato, setContato] = useState(corretor.contact_name);
  const [endereco, setEndereco] = useState<Address>(corretor.address);
  const [canal, setCanal] = useState(corretor.channel_type_id ?? '');
  const [codigoCorretor, setCodigoCorretor] = useState(
    corretor.insurer_broker_code ?? '',
  );
  const [codigoSucursal, setCodigoSucursal] = useState(
    corretor.insurer_branch_code ?? '',
  );
  const [codigoContrato, setCodigoContrato] = useState(
    corretor.insurer_contract_code ?? '',
  );
  const [nivel, setNivel] = useState(corretor.level ?? '');
  const [recadastroPendente, setRecadastroPendente] = useState(
    corretor.susep_reregistration_due ?? false,
  );
  const [compliancePendente, setCompliancePendente] = useState(
    corretor.compliance_pending ?? false,
  );
  // Dispensas do OnPoint (P-COR-3): guardadas sem efeito.
  const [dispensaCompliance, setDispensaCompliance] = useState(
    corretor.ignore_compliance_validation ?? false,
  );
  const [dispensaDocumentos, setDispensaDocumentos] = useState(
    corretor.ignore_pending_documents ?? false,
  );
  const [administrador, setAdministrador] = useState(
    corretor.administrator_member_id ?? '',
  );
  const [comercial, setComercial] = useState(corretor.commercial_owner);
  const [produtos, setProdutos] = useState<string[]>(corretor.product_ids);
  const [observacoes, setObservacoes] = useState(corretor.notes);

  const canais = useQuery({
    queryKey: ['broker-channel-types'],
    queryFn: ({ signal }) => brokers.channelTypes({}, signal),
  });
  const cnaes = useQuery({
    queryKey: ['catalogs', 'cnae', cnae],
    queryFn: ({ signal }) => catalogs.cnaeCodes(cnae, signal),
    enabled: podeEditar && cnae.replace(/\D/g, '').length >= 4,
    staleTime: 5 * 60 * 1000,
  });
  const pessoas = useQuery({
    queryKey: ['members', 'por-corretora', corretor.id],
    queryFn: ({ signal }) =>
      members.list({ broker_id: corretor.id, limit: 100 }, signal),
    enabled: podeEditar && can('usuarios.usuario.view'),
  });
  const listaProdutos = useQuery({
    queryKey: ['products', 'options'],
    queryFn: ({ signal }) => products.list(signal),
  });

  const canalEscolhido = (canais.data ?? []).find((item) => item.id === canal);
  // O canal inativo continua aparecendo no corretor que ja o tem, e nao e oferecido a
  // quem escolhe agora.
  const opcoesDeCanal = (canais.data ?? [])
    .filter((item) => item.active || item.id === corretor.channel_type_id)
    .map((item) => ({
      value: item.id,
      label: item.active ? item.name : `${item.name} (inativo)`,
    }));

  const opcoesDePessoas = (pessoas.data ?? []).map((pessoa) => ({
    value: pessoa.id,
    label: `${pessoa.name} (${pessoa.email})`,
  }));
  if (
    administrador &&
    !opcoesDePessoas.some((opcao) => opcao.value === administrador)
  ) {
    opcoesDePessoas.unshift({
      value: administrador,
      label: corretor.administrator
        ? `${corretor.administrator.name} (${corretor.administrator.email})`
        : 'Pessoa gravada',
    });
  }

  const erroComissao = rateInputError(comissao);
  const erroLimite = limite ? rateInputError(limite) : undefined;

  const salvar = useMutation({
    mutationFn: (entrada: BrokerInput) => brokers.update(corretor.id, entrada),
    onSuccess: () => {
      toast.success('Dados cadastrais salvos.');
      void queryClient.invalidateQueries({ queryKey: ['broker', corretor.id] });
      void queryClient.invalidateQueries({ queryKey: ['brokers'] });
      void queryClient.invalidateQueries({
        queryKey: ['entity-history', 'broker', corretor.id],
      });
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (erroComissao || erroLimite) return;
    salvar.mutate({
      legal_name: razaoSocial,
      trade_name: nomeFantasia,
      municipal_registration: inscricao,
      ...(podeComissao
        ? {
            commission_bps: rateToBasisPoints(comissao || '0'),
            max_commission_bps: limite ? rateToBasisPoints(limite) : null,
          }
        : {}),
      cnpj_status: situacaoCnpj,
      cnae_code: cnae,
      cnae_branch: ramo,
      legal_nature: naturezaJuridica,
      susep_registered_on: dataRegistro,
      reregistered_on: dataRecadastro,
      registration_expires_on: dataExpiracao,
      external_status: situacaoSusep,
      susep_code: codigoSusep,
      phone: telefone,
      email,
      contact_name: contato,
      address: endereco,
      channel_type_id: canal || null,
      insurer_broker_code: codigoCorretor,
      insurer_branch_code: codigoSucursal,
      insurer_contract_code: codigoContrato,
      level: nivel,
      susep_reregistration_due: recadastroPendente,
      compliance_pending: compliancePendente,
      ignore_compliance_validation: dispensaCompliance,
      ignore_pending_documents: dispensaDocumentos,
      administrator_member_id: administrador || null,
      commercial_owner: comercial,
      product_ids: produtos,
      notes: observacoes,
    });
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4">
      {salvar.error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {salvar.error.message}
        </Alert>
      ) : null}

      <DetailCard title="Dados cadastrais" description={AVISO_REGRAS_PENDENTES}>
        <fieldset
          disabled={!podeEditar || salvar.isPending}
          className="flex flex-col gap-5"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="CNPJ"
              readOnly
              value={formatDocument(corretor.document)}
              hint="So de leitura depois do cadastro."
            />
            <Field
              label="Razao social"
              required
              value={razaoSocial}
              error={fieldErrorOf(erro, 'legal_name')}
              onChange={(evento) => setRazaoSocial(evento.target.value)}
            />
            <Field
              label="Inscricao municipal"
              maxLength={30}
              value={inscricao}
              error={fieldErrorOf(erro, 'municipal_registration')}
              onChange={(evento) => setInscricao(evento.target.value)}
            />
            <Field
              label="Comissao (%)"
              inputMode="decimal"
              disabled={!podeComissao}
              value={comissao}
              error={erroComissao ?? fieldErrorOf(erro, 'commission_bps')}
              onChange={(evento) => setComissao(evento.target.value)}
            />
            <Field
              label="Limite de comissao (%)"
              inputMode="decimal"
              disabled={!podeComissao}
              value={limite}
              error={erroLimite ?? fieldErrorOf(erro, 'max_commission_bps')}
              onChange={(evento) => setLimite(evento.target.value)}
            />
            <Select
              label="Situacao do CNPJ"
              placeholder="Nao informada"
              options={situacoesDoCnpj}
              value={situacaoCnpj}
              error={fieldErrorOf(erro, 'cnpj_status')}
              hint={
                corretor.cnpj_status_checked_at
                  ? `Registrada em ${formatDateTime(corretor.cnpj_status_checked_at)}.`
                  : undefined
              }
              onChange={(evento) =>
                setSituacaoCnpj(evento.target.value as BrokerCnpjStatus)
              }
            />
            <Field
              label="Codigo CNAE"
              list={`cnae-${corretor.id}`}
              value={cnae}
              error={fieldErrorOf(erro, 'cnae_code')}
              hint={corretor.cnae_description || undefined}
              onChange={(evento) => setCnae(evento.target.value)}
            />
            <datalist id={`cnae-${corretor.id}`}>
              {(cnaes.data ?? []).map((item) => (
                <option key={item.code} value={item.code}>
                  {`${item.formatted_code} ${item.description}`}
                </option>
              ))}
            </datalist>
            <Field
              label="Ramo"
              maxLength={200}
              value={ramo}
              error={fieldErrorOf(erro, 'cnae_branch')}
              onChange={(evento) => setRamo(evento.target.value)}
            />
            <Field
              label="Data do registro"
              type="date"
              value={dataRegistro}
              error={fieldErrorOf(erro, 'susep_registered_on')}
              onChange={(evento) => setDataRegistro(evento.target.value)}
            />
            <Field
              label="Data do recadastro"
              type="date"
              value={dataRecadastro}
              error={fieldErrorOf(erro, 'reregistered_on')}
              hint={dataRecadastro ? undefined : 'Sem registro.'}
              onChange={(evento) => setDataRecadastro(evento.target.value)}
            />
            <Field
              label="Data expiracao do cadastro"
              type="date"
              value={dataExpiracao}
              error={fieldErrorOf(erro, 'registration_expires_on')}
              hint={
                corretor.registration_expired && corretor.registration_expires_on
                  ? `Expirou em ${formatCivilDate(corretor.registration_expires_on)}.`
                  : undefined
              }
              onChange={(evento) => setDataExpiracao(evento.target.value)}
            />
            <Select
              label="Situacao SUSEP"
              options={situacoesSusep}
              value={situacaoSusep}
              hint={
                corretor.susep_status_checked_at
                  ? `Registrada em ${formatDateTime(corretor.susep_status_checked_at)}.`
                  : 'Registrada a mao ate haver consulta a SUSEP.'
              }
              onChange={(evento) => setSituacaoSusep(evento.target.value)}
            />
            <Field
              label="Codigo SUSEP"
              value={codigoSusep}
              onChange={(evento) => setCodigoSusep(evento.target.value)}
            />
            <Field
              label="Telefone"
              value={telefone}
              onChange={(evento) => setTelefone(evento.target.value)}
            />
          </div>

          <AddressFields value={endereco} onChange={setEndereco} />

          <div className="grid gap-4 sm:grid-cols-3">
            <Select
              label="Canal"
              placeholder="Selecione um canal"
              options={opcoesDeCanal}
              value={canal}
              error={fieldErrorOf(erro, 'channel_type_id')}
              hint={canalEscolhido?.description || undefined}
              onChange={(evento) => setCanal(evento.target.value)}
            />
            <Field
              label="Codigo do corretor na seguradora"
              maxLength={30}
              required={canalEscolhido?.requires_insurer_codes === true}
              value={codigoCorretor}
              error={fieldErrorOf(erro, 'insurer_broker_code')}
              onChange={(evento) => setCodigoCorretor(evento.target.value)}
            />
            <Field
              label="Codigo da sucursal"
              maxLength={30}
              required={canalEscolhido?.requires_insurer_codes === true}
              value={codigoSucursal}
              error={fieldErrorOf(erro, 'insurer_branch_code')}
              onChange={(evento) => setCodigoSucursal(evento.target.value)}
            />
            <Field
              label="Codigo do contrato"
              maxLength={30}
              required={canalEscolhido?.requires_insurer_codes === true}
              value={codigoContrato}
              error={fieldErrorOf(erro, 'insurer_contract_code')}
              onChange={(evento) => setCodigoContrato(evento.target.value)}
            />
            <Field
              label="Level corretor"
              maxLength={60}
              value={nivel}
              error={fieldErrorOf(erro, 'level')}
              onChange={(evento) => setNivel(evento.target.value)}
            />
            <Field
              label="Natureza juridica"
              maxLength={200}
              value={naturezaJuridica}
              error={fieldErrorOf(erro, 'legal_nature')}
              onChange={(evento) => setNaturezaJuridica(evento.target.value)}
            />
            <Field
              label="Nome fantasia"
              value={nomeFantasia}
              onChange={(evento) => setNomeFantasia(evento.target.value)}
            />
            <Field
              label="Email"
              type="email"
              value={email}
              error={fieldErrorOf(erro, 'email')}
              onChange={(evento) => setEmail(evento.target.value)}
            />
            <Field
              label="Contato"
              value={contato}
              onChange={(evento) => setContato(evento.target.value)}
            />
            <Field
              label="Comercial responsavel"
              value={comercial}
              onChange={(evento) => setComercial(evento.target.value)}
            />
            {opcoesDePessoas.length > 0 ? (
              <Select
                label="Administrador do portal"
                placeholder="Nao definido"
                options={opcoesDePessoas}
                value={administrador}
                error={fieldErrorOf(erro, 'administrator_member_id')}
                onChange={(evento) => setAdministrador(evento.target.value)}
              />
            ) : (
              <Field
                label="Administrador do portal"
                readOnly
                value={
                  corretor.administrator
                    ? `${corretor.administrator.name} (${corretor.administrator.email})`
                    : 'Nao definido'
                }
              />
            )}
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-ink-700">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={recadastroPendente}
                onChange={(evento) => setRecadastroPendente(evento.target.checked)}
              />
              Recadastro SUSEP pendente
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={compliancePendente}
                onChange={(evento) => setCompliancePendente(evento.target.checked)}
              />
              Validacao de compliance pendente
            </label>
          </div>

          {/* P-COR-3: as dispensas da busca do OnPoint (COR-168, COR-169). Ficam
              gravadas e nao dispensam nada: a validacao de compliance e os documentos
              pendentes ainda sao regra pendente. */}
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-4 text-xs text-ink-700">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={dispensaCompliance}
                  onChange={(evento) => setDispensaCompliance(evento.target.checked)}
                />
                Ignorar validacao de compliance
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={dispensaDocumentos}
                  onChange={(evento) => setDispensaDocumentos(evento.target.checked)}
                />
                Ignorar documentos pendentes
              </label>
            </div>
            <p className="text-2xs text-ink-500">{PENDENCIA_DISPENSAS}</p>
          </div>

          {corretor.is_blocked ? (
            <Textarea
              label="Justificativa de bloqueio"
              readOnly
              value={corretor.block_reason ?? ''}
              hint="Registrada no bloqueio. Para mudar, desbloqueie e bloqueie de novo."
            />
          ) : null}

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-semibold text-ink-700">
              Produtos habilitados
            </legend>
            <p className="text-2xs text-ink-500">
              Sem nenhum marcado, o corretor opera qualquer produto da carteira.
            </p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(listaProdutos.data ?? []).map((produto) => (
                <label
                  key={produto.id}
                  className="flex items-center gap-2 text-xs text-ink-700"
                >
                  <input
                    type="checkbox"
                    checked={produtos.includes(produto.id)}
                    onChange={(evento) =>
                      setProdutos((atual) =>
                        evento.target.checked
                          ? [...atual, produto.id]
                          : atual.filter((item) => item !== produto.id),
                      )
                    }
                  />
                  <span className="truncate">{produto.name}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <Textarea
            label="Observacoes"
            value={observacoes}
            onChange={(evento) => setObservacoes(evento.target.value)}
          />
        </fieldset>

        {podeEditar ? (
          <div className="flex justify-end">
            <Button
              type="submit"
              loading={salvar.isPending}
              disabled={erroComissao !== undefined || erroLimite !== undefined}
            >
              Salvar
            </Button>
          </div>
        ) : null}
      </DetailCard>
    </form>
  );
}
